import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import { useSelector } from "react-redux";
import { formatAmount } from "../../../../Utils/CommonUtils";
import {
  useQuoteAgOptions,
  useQuoteMemberOptions,
} from "../Hook/useQuoteOptions";
import "../../Orders/Order.scss";

const RECIPIENT_EXISTING = "EXISTING";
const RECIPIENT_NEW = "NEW";

const channelOptions = [
  { value: "EMAIL", label: "Email" },
  { value: "WHATSAPP", label: "WhatsApp" },
];

const initialContact = { name: "", email_id: "", mobile: "" };

const SendQuoteModal = ({ show, onHide, onSend, isSending }) => {
  const formState = useSelector((state) => state.common.formRendererState);
  const selectedUser = useSelector((state) => state.header.selectedUser);
  const productAttributeData = useSelector(
    (state) => state.order.productAttributeData
  );
  const productData = useSelector((state) => state.product.productData);
  const productVariant = useSelector(
    (state) => state.order.selectedOrders.productVariant
  );
  const productVariantModalState = useSelector(
    (state) => state.order.productVariantModalState
  );

  const [recipientType, setRecipientType] = useState(RECIPIENT_EXISTING);
  const [memberId, setMemberId] = useState("");
  const [contact, setContact] = useState(initialContact);
  const [channels, setChannels] = useState(["EMAIL", "WHATSAPP"]);
  const [submitted, setSubmitted] = useState(false);

  const { data: memberOptions = [] } = useQuoteMemberOptions(
    formState.ag_id,
    show
  );
  const { data: agOptions = [] } = useQuoteAgOptions(
    selectedUser.organizationid,
    show
  );

  useEffect(() => {
    if (show) {
      setRecipientType(RECIPIENT_EXISTING);
      setMemberId("");
      setContact(initialContact);
      setChannels(["EMAIL", "WHATSAPP"]);
      setSubmitted(false);
    }
  }, [show]);

  const selectedMember = memberOptions.find((itm) => itm.value === memberId);

  const recipientName =
    recipientType === RECIPIENT_EXISTING
      ? selectedMember?.label?.replace(/\s*\(.*\)$/, "")
      : contact.name;

  const messagePreview = useMemo(() => {
    const productNames = productAttributeData?.length
      ? productAttributeData.map((item) => item.name).join(" + ")
      : productData?.find((item) => item.entityid === formState.product_id)
          ?.name;
    const agName = agOptions.find((itm) => itm.value === formState.ag_id)
      ?.label;
    const insurerName = productVariant?.content
      ?.flatMap((product) => product.product_variants || [])
      .find((variant) => variant.pspOem?.code === productVariantModalState?.oem)
      ?.pspOem?.name;
    const premium = productVariantModalState?.finalPremium;

    return [
      `Hi ${recipientName || "{name}"},`,
      "",
      `Here's your quote for ${productNames || "{product}"}${
        agName ? ` from ${agName}` : ""
      }.`,
      `Insurer: ${insurerName || productVariantModalState?.oem || "-"}`,
      `Premium Amount: ${
        premium ? formatAmount(premium, null, null, "lakh") : "-"
      }`,
    ].join("\n");
  }, [
    recipientName,
    productAttributeData,
    productData,
    formState.product_id,
    formState.ag_id,
    agOptions,
    productVariant,
    productVariantModalState,
  ]);

  const toggleChannel = (value) => {
    setChannels((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  const isMemberMissing = recipientType === RECIPIENT_EXISTING && !memberId;
  const isNameMissing = recipientType === RECIPIENT_NEW && !contact.name.trim();

  const handleSend = () => {
    setSubmitted(true);
    if (isMemberMissing || isNameMissing || channels.length === 0) return;

    onSend({
      recipient_type: recipientType,
      ...(recipientType === RECIPIENT_EXISTING
        ? { customer_id: memberId }
        : {
            name: contact.name.trim(),
            email_id: contact.email_id.trim(),
            mobile: contact.mobile.trim(),
          }),
      send_via: channels,
      message: messagePreview,
    });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
      className="border-0"
      modalClassName="modal fade zoomIn"
      size="lg"
    >
      <Form className="attributes-container">
        <Modal.Header className="p-3 bg-info-subtle" closeButton>
          <strong>Send Quote</strong>
        </Modal.Header>
        <Modal.Body className="modal-body">
          <div className="d-flex gap-2 mb-3">
            <Button
              type="button"
              className={
                recipientType === RECIPIENT_EXISTING ? "" : "primary-outline"
              }
              onClick={() => setRecipientType(RECIPIENT_EXISTING)}
            >
              Existing Member
            </Button>
            <Button
              type="button"
              className={
                recipientType === RECIPIENT_NEW ? "" : "primary-outline"
              }
              onClick={() => setRecipientType(RECIPIENT_NEW)}
            >
              New Contact
            </Button>
          </div>

          {recipientType === RECIPIENT_EXISTING ? (
            <Row>
              <Form.Group as={Col} md={6} className="mb-3">
                <Form.Label>
                  Select member<span className="text-danger">*</span>
                </Form.Label>
                <Select
                  placeholder="Select"
                  options={memberOptions}
                  value={selectedMember || null}
                  onChange={(e) => setMemberId(e ? e.value : "")}
                  className={submitted && isMemberMissing ? "is-invalid" : ""}
                />
                {submitted && isMemberMissing && (
                  <div className="invalid-feedback">This field is required</div>
                )}
              </Form.Group>
            </Row>
          ) : (
            <Row>
              <Form.Group as={Col} md={6} className="mb-3">
                <Form.Label>
                  Name<span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter"
                  value={contact.name}
                  isInvalid={submitted && isNameMissing}
                  onChange={(e) =>
                    setContact({ ...contact, name: e.target.value })
                  }
                />
                <Form.Control.Feedback type="invalid">
                  This field is required
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} md={6} className="mb-3">
                <Form.Label>Email id</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter"
                  value={contact.email_id}
                  onChange={(e) =>
                    setContact({ ...contact, email_id: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group as={Col} md={6} className="mb-3">
                <Form.Label>Mobile No.</Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="Enter"
                  value={contact.mobile}
                  onChange={(e) =>
                    setContact({
                      ...contact,
                      mobile: e.target.value.replace(/\D/g, ""),
                    })
                  }
                />
              </Form.Group>
            </Row>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Send Via</Form.Label>
            <div className="d-flex gap-4">
              {channelOptions.map((channel) => (
                <Form.Check
                  key={channel.value}
                  type="checkbox"
                  id={`send-via-${channel.value}`}
                  label={channel.label}
                  checked={channels.includes(channel.value)}
                  onChange={() => toggleChannel(channel.value)}
                />
              ))}
            </div>
            {submitted && channels.length === 0 && (
              <div className="text-danger">Select at least one channel</div>
            )}
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label>Message Preview</Form.Label>
            <Form.Control as="textarea" rows={4} value={messagePreview} readOnly />
          </Form.Group>
          <div className="form-text text-muted">
            The quote PDF is attached to emails, WhatsApp gets a secure link.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" onClick={handleSend} disabled={isSending}>
            {isSending ? "Sending..." : "Send Quote"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SendQuoteModal;
